import { useEffect, useState } from 'react';
import { getJson } from '../api/client';
import { Card, EmptyState, Spinner } from '../components/ui';

interface Ponto {
  rotulo: string;
  saldoFinal: number | null;
  resultado: number | null;
  fundosTotal: number;
  inadimplenciaPct: number;
  gasConsumoMili: number;
}
interface Resumo {
  meses: number;
  saldoFinalAtual: number | null;
  resultadoAtual: number | null;
  fundosAtual: number;
  inadimplenciaMediaPct: number;
  inadimplenciaAtualPct: number;
  gasConsumoMedioMili: number;
}
interface Indicadores {
  serie: Ponto[];
  resumo: Resumo;
}

const brl = (c: number | null | undefined) =>
  c == null ? '—' : (c / 100).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
const m3 = (mili: number) =>
  `${(mili / 1000).toLocaleString('pt-BR', { maximumFractionDigits: 3 })} m³`;
const pct = (v: number) => `${v.toLocaleString('pt-BR', { maximumFractionDigits: 2 })}%`;

/** Mini gráfico de barras (CSS puro, sem dependências). */
function Barras({
  titulo,
  pontos,
  valor,
  formata,
}: {
  titulo: string;
  pontos: Ponto[];
  valor: (p: Ponto) => number;
  formata: (n: number) => string;
}) {
  const valores = pontos.map(valor);
  const max = Math.max(1, ...valores);
  return (
    <Card>
      <h3 style={{ marginTop: 0 }}>{titulo}</h3>
      <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'flex-end', height: 120 }}>
        {pontos.map((p) => {
          const v = valor(p);
          const h = Math.round((v / max) * 100);
          return (
            <div
              key={p.rotulo}
              style={{
                flex: 1,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: 4,
              }}
              title={`${p.rotulo}: ${formata(v)}`}
            >
              <div
                style={{
                  width: '100%',
                  height: `${h}%`,
                  minHeight: 2,
                  background: 'var(--brand, #4f46e5)',
                  borderRadius: '4px 4px 0 0',
                }}
                aria-hidden="true"
              />
              <span style={{ fontSize: '0.7rem', opacity: 0.7 }}>{p.rotulo}</span>
            </div>
          );
        })}
      </div>
    </Card>
  );
}

function KPI({ label, valor }: { label: string; valor: string }) {
  return (
    <Card>
      <div style={{ fontSize: '0.8rem', opacity: 0.7 }}>{label}</div>
      <div style={{ fontSize: 'var(--step-2)', fontWeight: 700 }}>{valor}</div>
    </Card>
  );
}

/** Dashboards históricos e indicadores (#82). Só agregados — sem PII. */
export function Indicadores() {
  const [data, setData] = useState<Indicadores | null>(null);
  const [carregando, setCarregando] = useState(true);

  useEffect(() => {
    void getJson<Indicadores>('/api/indicadores/historico').then((d) => {
      setData(d);
      setCarregando(false);
    });
  }, []);

  if (carregando) return <Spinner />;
  if (!data || data.serie.length === 0)
    return <EmptyState>Ainda não há balancetes publicados para gerar indicadores.</EmptyState>;

  const { serie, resumo } = data;
  return (
    <div style={{ display: 'grid', gap: 'var(--space-4)' }}>
      <div
        style={{
          display: 'grid',
          gap: 'var(--space-3)',
          gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))',
        }}
      >
        <KPI label="Saldo final atual" valor={brl(resumo.saldoFinalAtual)} />
        <KPI label="Resultado do mês" valor={brl(resumo.resultadoAtual)} />
        <KPI label="Fundos (atual)" valor={brl(resumo.fundosAtual)} />
        <KPI label="Inadimplência atual" valor={pct(resumo.inadimplenciaAtualPct)} />
        <KPI label="Inadimplência média" valor={pct(resumo.inadimplenciaMediaPct)} />
        <KPI label="Gás médio/mês" valor={m3(resumo.gasConsumoMedioMili)} />
      </div>

      <Barras titulo="Saldo final" pontos={serie} valor={(p) => p.saldoFinal ?? 0} formata={brl} />
      <Barras
        titulo="Inadimplência (%)"
        pontos={serie}
        valor={(p) => p.inadimplenciaPct}
        formata={pct}
      />
      <Barras titulo="Fundos" pontos={serie} valor={(p) => p.fundosTotal} formata={brl} />
      <Barras titulo="Consumo de gás" pontos={serie} valor={(p) => p.gasConsumoMili} formata={m3} />
    </div>
  );
}
